/**
 * useApiToken.ts — bridges MSAL token acquisition to the API client's
 * injected `TokenGetter`. Returns a stable async function that silently
 * acquires a BFF access token for the active account, falling back to an
 * interactive redirect if the silent path needs interaction.
 */

import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback } from 'react';
import type { TokenGetter } from '../api/client';
import { apiTokenRequest } from './msalConfig';

export const useApiToken = (): TokenGetter => {
  const { instance, accounts } = useMsal();

  return useCallback<TokenGetter>(async () => {
    const account = accounts[0] ?? instance.getActiveAccount() ?? undefined;
    if (!account) {
      throw new Error('No active account — login required before acquiring a token.');
    }
    try {
      const result = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...apiTokenRequest, account });
      }
      throw error;
    }
  }, [instance, accounts]);
};
